import { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import './Marketplace.css';

const reasons = [
  {
    number: '01',
    title: 'A Rising Economic Powerhouse',
    desc: 'New economic zones such as Kribi and Limbe are transforming into Cameroon\'s next major economic hubs, driven by the Deep Sea Port, expanding industrial zones, and new road and energy infrastructure linking them to Douala and Yaoundé. This growth is already pushing land and property values upward.',
  },
  {
    number: '02',
    title: 'A Premier Tourism & Lifestyle Destination',
    desc: 'With white-sand beaches, the iconic Lobé Waterfalls, and increasing hotel and resort development, Kribi alone is becoming one of Central Africa\'s most desirable coastal destinations. This fuels strong demand for vacation homes, rentals, and resort-style living.',
  },
  {
    number: '03',
    title: 'Early-Stage Market With High Appreciation',
    desc: 'Kribi, Limbe and other Cameroonian cities along the Atlantic Coast are still in the early phase of their real estate cycle. Investors benefit from lower entry prices, higher long-term capital gains, and first-mover advantage in premium beachfront and second-line locations.',
  },
  {
    number: '04',
    title: 'Built for the Diaspora: Secure, Transparent, Tokenized',
    desc: 'Diaspora buyers want verified titles, secure payments, and transparent processes. Tokenization platforms like Azamra add digital escrow, traceability, and the ability to pledge tokens as collateral — reducing traditional risks and making investment far more accessible.',
  },
];

const Marketplace = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>Marketplace — Azamra</title>
        <meta name="description" content="Cameroon Waterfront Living Redefined. Own a piece of paradise — a world-class coastal residential community on Cameroon's coastline, powered by Azamra's tokenized real estate platform." />
        <meta property="og:title" content="Marketplace — Azamra" />
        <meta property="og:description" content="Cameroon Waterfront Living Redefined. Own a piece of paradise — a world-class coastal residential community on Cameroon's coastline." />
        <meta property="og:url" content="https://www.azamra.com/marketplace" />
        <link rel="canonical" href="https://www.azamra.com/marketplace" />
      </Helmet>
      <Header />

      {/* ── SECTION 1: HERO ── */}
      <section
        className="mp-hero-section"
        style={{ backgroundImage: `url('/assets/images/kribi-sunset.webp')` }}
      >
        <div className="mp-hero-overlay" />
        <Container>
          <Row className="mp-hero-row">
            <Col lg={8} md={12} xs={12} className="mp-hero-left">
              <span className="mp-hero-tag">Azamra Marketplace</span>
              <h1 className="mp-hero-title">
                Coming Soon: Cameroon Waterfront Living Redefined
              </h1>
              <p className="mp-hero-subtitle">
                Own a piece of paradise at home. A new era of coastal luxury is coming soon on the shores of one of Cameroon's coastline, designed for Cameroonians at home and those in the diaspora.
              </p>
              <div className="mp-hero-actions">
                <button
                  className="mp-hero-btn"
                  onClick={() => navigate('/marketplace/azamra-homes')}
                >
                  Explore Kribi Homes
                </button>
                <a
                  href="/marketplace/azamra-homes#register-interest"
                  className="mp-hero-btn-outline"
                >
                  Register Your Interest
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── SECTION 2: PROJECT INTRO ── */}
      <section className="mp-intro-section">
        <Container>
          <Row className="align-items-center mp-intro-row">

            <Col lg={6} md={12} className="mp-intro-left">
              <span className="mp-intro-label">Introducing the Kribi Beach Project</span>
              <h2 className="mp-intro-title">
                A World-Class Community on Africa's Atlantic Coast
              </h2>
              <p className="mp-intro-para">
                Offering a world-class residential and commercial community crafted with veteran developers from the USA, Kenya, and South Africa, bringing decades of global excellence to Cameroon's most iconic coastline.
              </p>
              <p className="mp-intro-para">
                With modern tropical architecture, direct beach access, secure gated living, and resort-grade amenities — all powered by Azamra's fully tokenised real estate platform.
              </p>
              <p className="mp-intro-para mp-intro-para-bold">
                Buy your dream home with confidence, transparency, and security.
              </p>
              <button
                className="mp-intro-btn"
                onClick={() => navigate('/marketplace/azamra-homes')}
              >
                View the Project
              </button>
            </Col>

            <Col lg={6} md={12} className="mp-intro-right">
              <div className="mp-intro-img-wrap">
                <img
                  src="/assets/images/kribi-aerial-beach.webp"
                  alt="Lobé Waterfalls, Kribi"
                  className="mp-intro-img"
                />
              </div>
            </Col>

          </Row>
          <div className="mp-section-divider" />
        </Container>
      </section>

      {/* ── SECTION 3: WHY KRIBI ── */}
      <section className="mp-why-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} md={10} xs={12} className="text-center">
              <h2 className="mp-why-title">Why Invest in Tokenized Real Estate in Cameroon?</h2>
              <p className="mp-why-desc">
                Four compelling reasons why Cameroon's Atlantic Coast is Africa's most exciting real estate opportunity right now.
              </p>
            </Col>
          </Row>
          <Row className="mp-why-row">
            {reasons.map((item) => (
              <Col lg={6} md={6} xs={12} className="mp-why-col" key={item.number}>
                <div className="mp-why-card">
                  <span className="mp-why-number">{item.number}</span>
                  <h3 className="mp-why-card-title">{item.title}</h3>
                  <p className="mp-why-card-desc">{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="mp-cta-section">
        <Container>
          <div
            className="mp-cta-banner"
            style={{ backgroundImage: `url('/assets/images/${isMobile ? 'cta1.webp' : 'cta.webp'}')` }}
          >
            <h2 className="mp-cta-title">
              <span className="mp-cta-title-bold">Your home on Africa's Atlantic coast</span>
              <span className="mp-cta-title-regular">is waiting. Register your interest today.</span>
            </h2>
            <div className="mp-cta-badges">
              <a href="/marketplace/azamra-homes#register-interest" className="cta-demo-btn">
                Register Your Interest
              </a>
            </div>
          </div>
        </Container>
      </section>

      <Footer />
    </>
  );
};

export default Marketplace;
